import { createApi } from '@reduxjs/toolkit/query/react';
import { spotlightApiClient } from 'src/clients/spotlight-api-client';
import { createBaseQuery } from 'src/store/rtk-query-base';

export interface InvokeSpotlightJobRequest {
  countryCode: string;
  workflowData: {
    partnerName: string;
    seedSegmentId: string;
    seedSegmentType: string;
    exclusionListSegmentId: string;
    exclusionListSegmentType: string;
    audienceName: string;
    advertiserAccountIds: string[];
  };
}

export interface InvokeSpotlightJobResponse {
  statusCode: number;
  body: {
    executionArn: string;
    startDate: string;
    success: boolean;
    message: string;
  };
}

export interface SpotlightJobHistoryItem {
  executionId: string;
  country: string;
  alias: string;
  statusMessage: string | null;
  status: string;
  partner: string;
  outputSegmentName: string;
  seedSegmentId: string;
  seedSegmentType: string;
  exclusionListSegmentId: string | null;
  exclusionListSegmentType: string | null;
  errorMessage: string | null;
  accountIds: string[] | null;
  recallScore: number | null;
  createdAt: string;
  updatedAt: string;
}

export type GetSpotlightJobHistoryResponse = SpotlightJobHistoryItem[];

export const transformJobHistoryResponse = (response: unknown): GetSpotlightJobHistoryResponse => {
  if (Array.isArray(response)) return response;
  const res = response as Record<string, unknown>;
  const body = (res?.body ?? res) as Record<string, unknown>;
  if (Array.isArray(body?.jobHistories)) return body.jobHistories as GetSpotlightJobHistoryResponse;
  return [];
};

export const spotlightApi = createApi({
  reducerPath: 'spotlightApi',
  baseQuery: createBaseQuery(spotlightApiClient),
  tagTypes: ['SpotlightJobHistory'],
  endpoints: (builder) => ({
    invokeSpotlightJob: builder.mutation<InvokeSpotlightJobResponse, InvokeSpotlightJobRequest>({
      query: (body) => ({
        url: '/invoke',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SpotlightJobHistory'],
    }),
    getSpotlightJobHistory: builder.query<GetSpotlightJobHistoryResponse, void>({
      query: () => ({
        url: '/history',
        method: 'GET',
      }),
      transformResponse: transformJobHistoryResponse,
      providesTags: ['SpotlightJobHistory'],
    }),
  }),
});

export const { useInvokeSpotlightJobMutation, useGetSpotlightJobHistoryQuery } = spotlightApi;
